<template>
  <div class="booking">
    <div class="booking__wrap">
      <vinput
        :value="room_name"
        :border="true"
        :placeholder="$tr('onlineInterview.room.nameInterview')"
        @input="set_name"
      />

      <div class="booking__date">
        <div class="calendar">
          <masked-input
            :mask="
              is_japan_locale
                ? [
                    /[1-2]/,
                    /[0-9]/,
                    /[0-9]/,
                    /[0-9]/,
                    '-',
                    /[0-2]/,
                    /[0-9]/,
                    '-',
                    /[0-3]/,
                    /[0-9]/
                  ]
                : [
                    /[0-3]/,
                    /[0-9]/,
                    '-',
                    /[0-2]/,
                    /[0-9]/,
                    '-',
                    /[1-2]/,
                    /[0-9]/,
                    /[0-9]/,
                    /[0-9]/
                  ]
            "
            ref="masked"
            class="calendar__date-input"
            name="date"
            v-model="date"
            :keepCharPositions="true"
          />
          <img
            @click.prevent.stop="show_picker = !show_picker"
            src="/static/images/elements/calendar_2.svg"
            alt="Calendar icon"
          />

          <calendar
            v-if="show_picker"
            v-on-click-away="hide_picker"
            class="calendar__date-range"
            :show-lunar="false"
            :first-day-of-week="1"
            :lang="current_locale"
            :monthYearFormat="dateFormatPicker"
            @change="change"
          />
        </div>

        <div class="time">
          <multiselect
            track-by="id"
            label="name"
            class="calendar__multiselect"
            v-model="time"
            :options="options"
            :time_format="time_format"
            :icon_select="true"
            :show-labels="false"
            :allow-empty="false"
            :searchable="true"
            :taggable="true"
            @open="open_time_picker"
            @tag="add_time"
            :placeholder="''"
          />
        </div>
      </div>

      <vbutton
        class="booking__btn"
        label="onlineInterview.room.create"
        :icon="'plus_w'"
        :loading="loading"
        @click="create"
      />

      <input
        class="booking__link"
        :value="room_link"
        :placeholder="$tr('onlineInterview.room.link')"
        readonly
      />

      <div v-if="room_link" class="booking__link-label">
        {{ $tr('onlineInterview.room.duration') }}
      </div>
    </div>

    <div class="booking__btns">
      <vbutton
        class="btn"
        :icon="'icon_copy2'"
        :color="'transparent'"
        :disabled="!room_link"
        @click="copyLink"
      />
      <vbutton
        class="btn"
        :icon="'icon_room'"
        :color="'transparent'"
        :disabled="!room_link"
        @click="setLocationTo"
      />
    </div>
  </div>
</template>

<script>
  import Axios from 'axios';
  import Moment from 'moment';
  import MaskedInput from 'vue-text-mask';
  import { Calendar } from 'vue-date-range';
  import { directive as VueClickAway } from 'vue-clickaway';
  import CopyTextToClipboard from '@/components/mixin/CopyTextToClipboard.js';

  export default {
    name: 'booking',

    components: {
      Calendar,
      MaskedInput
    },

    directives: {
      onClickAway: VueClickAway
    },

    mixins: [CopyTextToClipboard],

    props: {
      room: {
        type: Object,
        default: null
      },
      time_format: {
        type: Number,
        default: 24
      },
      current_locale: {
        type: String,
        default: 'ru'
      }
    },

    data() {
      return {
        show_picker: false,
        room_name: $tr('online-interview.name'),
        current_date: null,
        current_time: null,
        today: null
      };
    },

    created() {
      this.current_data();
      this.current_date = Moment().format(this.dateFormat);
      this.$store.commit('online_interview/set_current_date', this.date);
    },

    computed: {
      loading() {
        return this.$store.getters['online_interview/loading'];
      },

      is_japan_locale() {
        return this.$store.getters['locale/is_japan_locale'];
      },

      dateFormat() {
        return this.is_japan_locale ? 'YYYY-MM-DD' : 'DD-MM-YYYY';
      },

      dateFormatPicker() {
        return this.is_japan_locale ? 'YYYY - MM' : 'MM - YYYY';
      },

      time: {
        get() {
          return this.current_time;
        },
        set(v) {
          this.current_time = v;
        }
      },

      mask() {
        return this.is_japan_locale
          ? /[1-2][0-9][0-9][0-9]-[0-2][0-9]-[0-3][0-9]/g
          : /[0-3][0-9]-[0-2][0-9]-[1-2][0-9][0-9][0-9]/g;
      },

      is_date_valid() {
        return (
          Moment(this.date, this.dateFormat)._isValid &&
          !!this.date.match(this.mask)
        );
      },

      is_time_valid() {
        if (
          this.time_format === 12 &&
          this.current_date <= Moment().format(this.dateFormat)
        ) {
          return !!this.time.name.match(/([0-9][0-9]|[0-9]):[0-5][0-9] [AP]M/g);
        } else {
          return !!this.time.name.match(/([0-9][0-9]|[0-9]):[0-5][0-9]/g);
        }
      },

      date: {
        get() {
          return this.current_date;
        },
        set(v) {
          if (v.match(this.mask)) {
            const date = Moment();

            if (Moment(v, this.dateFormat) >= date) {
              this.current_date = v;
              this.$store.commit(
                'online_interview/set_current_date',
                this.date
              );
            } else if (!Moment(v, this.dateFormat)._isValid) {
              this.current_date = date.format(this.dateFormat);
              this.$refs.masked.$refs.input.value = this.current_date;
            } else {
              this.current_date = date.format(this.dateFormat);
            }
          } else {
            this.current_date = v;
          }
        }
      },

      all_time() {
        if (this.time_format === 24) {
          const day = this.is_date_valid
            ? Moment(this.current_date, this.dateFormat).format('YYYY-MM-DD')
            : Moment().format('YYYY-MM-DD');
          const time = this.is_time_valid
            ? `${this.time.name}:00`
            : `${this.today.name}:00`;
          return `${day} ${time}`;
        } else {
          const day = this.is_date_valid
            ? Moment(this.current_date, this.dateFormat).format('YYYY-MM-DD')
            : Moment().format('YYYY-MM-DD');
          const time = this.is_time_valid
            ? Moment(this.timeConversion(this.time.name), 'hh:mm').format(
                'HH:mm'
              )
            : Moment(this.timeConversion(this.today.name), 'hh:mm').format(
                'HH:mm'
              );
          return `${day} ${time}:00`;
        }
      },

      room_link() {
        return this.room
          ? `${window.location.origin}/online/${this.room.token}`
          : '';
      },

      format_24() {
        let result = [];

        for (let i = 0; i <= 23; i++) {
          let odd = {
            id: `${i}_odd`,
            name: `${i}:00`,
            type: 'clock'
          };
          let even = {
            id: `${i}_even`,
            name: `${i}:30`,
            type: 'clock'
          };

          result.push(odd, even);
        }
        return result;
      },

      filter_format_24() {
        let moment = this.today.name.split(':')[0];

        return this.format_24.filter(e => +e.name.split(':')[0] > +moment);
      },

      format_12() {
        let result = [];

        for (let i = 0; i <= 23; i++) {
          if (i === 0) {
            let odd = {
              id: `${i}_odd`,
              name: `12:00 AM`,
              type: 'clock'
            };
            let even = {
              id: `${i}_even`,
              name: `12:30 AM`,
              type: 'clock'
            };
            result.push(odd, even);
          } else {
            let val = {};
            if (i <= 11) {
              val = {
                odd: `${i}:00 AM`,
                even: `${i}:30 AM`
              };
            } else if (i === 12) {
              val = {
                odd: `12:00 PM`,
                even: `12:30 PM`
              };
            } else {
              val = {
                odd: `${i - 12}:00 PM`,
                even: `${i - 12}:30 PM`
              };
            }
            let odd = {
              id: `${i}_odd`,
              name: val.odd,
              type: 'clock'
            };
            let even = {
              id: `${i}_even`,
              name: val.even,
              type: 'clock'
            };
            result.push(odd, even);
          }
        }
        return result;
      },

      filter_format_12() {
        let result = [];
        let today_date = Moment(this.timeConversion(this.today.name), 'hh:mm');

        this.format_12.forEach((el, index) => {
          let elem = Moment(this.timeConversion(el.name), 'hh:mm');
          if (elem > today_date) {
            result.push(el);
          }
        });
        return result;
      },

      options() {
        if (this.time_format === 24) {
          return this.current_date > Moment().format(this.dateFormat)
            ? this.format_24
            : this.filter_format_24;
        } else {
          return this.current_date > Moment().format(this.dateFormat)
            ? this.format_12
            : this.filter_format_12;
        }
      }
    },

    watch: {
      room_link: {
        immediate: true,
        handler(val) {
          this.$emit('change_room_link', val);
        }
      }
    },

    methods: {
      create() {
        if (!this.room_name) {
          this.room_name = this.$tr('online-interview.name');
        } else if (!this.current_date) {
          this.current_date = Moment().format(this.dateFormat);
        }

        this.$emit('create', {
          date_start: this.all_time,
          name: this.room_name
        });
      },

      open_time_picker() {
        this.hide_picker();
      },

      add_time(val, user = null) {
        if (this.time_format === 24) {
          if (this.current_date > Moment().format(this.dateFormat)) {
            // this.format_24
            if (val.match(/([0-9][0-9]|[0-9]):[0-5][0-9]/g)) {
              this.time = {
                id: Moment().unix(),
                name: val,
                type: 'clock'
              };
            }
          } else {
            let moment = this.today.name.split(' ')[0];
            if (Moment(val, 'hh:mm') > Moment(moment, 'hh:mm')) {
              if (val.match(/([0-9][0-9]|[0-9]):[0-5][0-9]/g)) {
                this.time = {
                  id: Moment().unix(),
                  name: val,
                  type: 'clock'
                };
              }
            } else {
              this.time = this.today;
            }
          }
        } else {
          if (this.current_date > Moment().format(this.dateFormat)) {
            if (val.match(/([0-9][0-9]|[0-9]):[0-5][0-9] [AP]M/g)) {
              this.time = {
                id: Moment().unix(),
                name: val,
                type: 'clock'
              };
            }
          } else {
            if (val.match(/([0-9][0-9]|[0-9]):[0-5][0-9] [AP]M/g)) {
              let today_date = Moment(
                this.timeConversion(this.today.name),
                'hh:mm'
              );
              let val_date = Moment(this.timeConversion(val), 'hh:mm');

              if (val_date > today_date) {
                if (val.match(/([0-9][0-9]|[0-9]):[0-5][0-9] [AP]M/g)) {
                  this.time = {
                    id: Moment().unix(),
                    name: val,
                    type: 'clock'
                  };
                }
              }
            }
          }
        }
      },

      timeConversion(s) {
        let time = s.split(':');
        var min = time[1];
        if (s.match('PM')) {
          var hour = 12 + parseInt(time[0], 10);
          hour = hour == 24 ? 12 : hour;
        } else {
          var hour = time[0] == 12 ? '00' : time[0];
        }
        if (hour < 10) hour = hour;
        let newTime = hour + ':' + min.split(' ')[0];
        return newTime;
      },

      copyLink() {
        if (this.room_link) {
          this.copyLinkToClipboard(this.room_link);
        }
      },

      setLocationTo() {
        if (this.room_link !== '') {
          window.open(this.room_link, '_blank');
        }
      },

      hide_picker() {
        if (!this.show_picker) return;

        this.show_picker = false;
      },

      set_name(v) {
        this.room_name = v;
      },

      change(v) {
        const new_date = Moment(v).format(this.dateFormat);
        const date = Moment().format(this.dateFormat);

        if (new_date >= date) {
          this.current_date = new_date;
          this.$store.commit('online_interview/set_current_date', this.date);
        }
      },

      current_data() {
        this.current_time = {
          id: 0,
          name:
            this.time_format === 24
              ? Moment().format('HH:mm')
              : Moment()
                  .locale('en')
                  .format('h:mm a')
                  .toUpperCase(),
          type: 'clock'
        };
        this.today = {
          id: Moment().unix(),
          name:
            this.time_format === 24
              ? Moment().format('HH:mm')
              : Moment()
                  .locale('en')
                  .format('h:mm a')
                  .toUpperCase(),
          type: 'clock'
        };
      }
    }
  };
</script>

<style lang="scss" scoped>
  .booking {
    width: 570px;
    display: flex;
    align-items: flex-end;

    &__wrap {
      width: 350px;
      position: relative;
    }

    &__date {
      display: flex;
      justify-content: space-between;
      margin: 20px 0;

      .calendar {
        position: relative;
        display: flex;
        align-items: center;

        img {
          display: inline-block;
          cursor: pointer;
          position: absolute;
          left: 29px;
          top: 50%;
          transform: translateY(-50%);
        }

        &__date-input {
          width: 160px;
          padding: 10px 10px 10px 55px;
          border: 1px solid #d2d6e0;
          color: #232323;
        }

        &__date-range {
          position: absolute;
          left: 0;
          top: calc(100% + 5px);
          z-index: 99;
        }

        &__multiselect {
          width: 160px;
        }
      }
    }

    &__link {
      width: 100%;
      border: 1px solid #d2d6e0;
      border-radius: 2px;
      color: #233346;
    }

    &__link-label {
      color: #9faab6;
      font-size: 15px;
      line-height: 22px;
      position: absolute;
      bottom: -24px;
    }

    &__btn {
      width: 100%;
      margin-bottom: 20px;
    }

    &__btns {
      display: flex;
      margin-left: 10px;

      .btn {
        margin-left: 9px;

        &:focus,
        &:active {
          outline: 0;
          border: 0;
        }
      }
    }
  }
</style>
